// © Microsoft Corporation. All rights reserved.

/** @jsx h */ /* Note: Required to be at the top of the file along with the import h */
import h from "@msnews/wc-jsx";

import { BaseExperience, InitialCommonExperienceProps, blackHexColor, whiteHexColor } from "@msnews/experiences-base";
import { CommonHeaderHandledProps, CommonHeaderUnhandledProps, ContextualNavItem } from "./CommonHeader.props";

import { MicrosoftLogoIcon } from "./CommonHeader.icons";
import { FeedNavigation, FeedNavigationData } from "@msnews/feed-navigation";
import { getAppEnvironment, HandledPropsKeys } from "@msnews/core";
import { DesignSystem, neutralLayerFloating, DesignSystemDefaults, neutralLayerCardContainer } from "@microsoft/fast-components-styles-msft";

import { CommonHeaderProps } from "./CommonHeader.props";
import { SearchBox } from "@msnews/search-box";
import { CommonHeaderRenderError } from "@msnews/diagnostics";
import { compareDisplayNameDesc, KeyMetricTags, windowsMarkets } from "@msnews/experiences-constants";
import { TelemetryActionType, TelemetryBehaviorType, Telemetry } from "@msnews/telemetry-contracts";
import { FluentDivider, FluentDesignSystemProvider } from "@fluentui/web-components";
import { AutoSuggestDelayBinding, searchBoxId, AutoSuggestInitProperties, initializeAutoSuggest, searchInputId } from "@msnews/autosuggest";
import { Direction, keyCodeEnter, keyCodeEscape, keyCodeSpace } from "@microsoft/fast-web-utilities";
import { DesignSystemProvider } from "@microsoft/fast-jss-manager-react";
import { MeControlComponent } from "@msnews/me-control-component";
import { MsftHamburgerMenu, MsftHamburgerMenuItem } from "@fast/components";
import { Settings, SettingsProps as BaseSettingsProps } from "@msnews/settings";
import { Select, SelectOption } from "@microsoft/fast-components-react-msft";
import { MarketSelectorStyles } from "./CommonHeader.styles";
import { TelemetryDOM } from "@msnews/telemetry-core";

// Prevent tree shaking
FluentDesignSystemProvider;
FluentDivider;
MsftHamburgerMenuItem;

const languageSelectorId = "languageSelector";

/**
 * Common header experience
 */
export class CommonHeader extends BaseExperience<CommonHeaderHandledProps, CommonHeaderUnhandledProps, CommonHeaderComponentState> {
    /** The CommonHeader default prop contract. Describes any default values for any component handled props */
    public static defaultProps: Partial<CommonHeaderHandledProps> = {
        experienceName: "CommonHeader"
    };

    /** Ref for language selector */
    private marketSelectorRef: HTMLElement;

    /** Sorted list of markets for the language selector */
    private markets = windowsMarkets.sort(compareDisplayNameDesc);

    /** An enumeration of all handled props. All props passed to the component that are not enumerated here will be treated as unhandled props */
    protected handledProps: HandledPropsKeys<CommonHeaderHandledProps> = {
        ...InitialCommonExperienceProps,
        managedClasses: undefined,
        hamburgerMenuProps: undefined,
        contextualNavProps: undefined,
        contextualSecondaryNavProps: undefined,
        isContextualSecondaryNavEnabled: undefined,
        searchProps: undefined,
        meControlProps: undefined,
        handleRenderInfo: undefined,
        selectedNavItem: undefined,
        selectedSecondaryNavItem: undefined,
        selectedNavMenuItem: undefined,
        selectedSecondaryNavMenuItem: undefined,
        goToMyFeed: undefined,
        disableFirstRow: undefined,
        appErrorCallback: undefined,
        settingsProps: undefined,
        disableDarkmode: undefined,
        includeWrapperDesignSystemProvider: undefined,
        lightLogo: undefined,
        darkLogo: undefined,
        commonHeaderTelemetryContext: undefined,
        logoAltText: undefined
    };

    /** HTML Element for search box input element */
    protected inputRef: HTMLElement;

    /** HTML Element for search box form element */
    protected formRef: HTMLElement;

    /** Autosuggest event handler */
    protected autoSuggestEventHandler: EventListener;

    /** Autosuggest register on search handler */
    protected registerAutoSuggestOnSearchEventHandler: EventListener;

    /** Autosuggest suggestion click handler */
    protected onAutoSuggestSuggestionClickHandler: EventListener;

    /** Listener for keypress events */
    protected onKeypressHandler: EventListener;

    /**
     * Reference to current design system
     */
    protected currentDesignSystem: DesignSystem;

    constructor(props: CommonHeaderProps) {
        super(props);

        if (props.disableDarkmode) {
            this.state = {
                isDarkMode: false,
                isMenuOpen: false
            };
            return;
        }

        const darkModeQuery = matchMedia("(prefers-color-scheme:dark)");

        this.state = {
            isDarkMode: darkModeQuery.matches,
            isMenuOpen: false
        };

        // Handle dark mode toggles
        darkModeQuery.addEventListener("change", (e: MediaQueryListEvent) => {
            this.setState({ isDarkMode: e.matches });
        });
    }

    /**
     * React life-cycle method called when updates are received after initial render
     */
    public componentDidUpdate(prevProps: CommonHeaderProps): void {
        super.componentDidUpdate();

        if (this.props.disableFirstRow) {
            return;
        }

        const { searchProps } = prevProps;
        if (searchProps && searchProps.autoSuggest && searchProps.autoSuggest.delayBind === AutoSuggestDelayBinding.OnBusinessSearchResultsReady) {
            const autoSuggestInitProps = this.getAutoSuggestInitProps();
            initializeAutoSuggest(autoSuggestInitProps);
        }
    }

    /**
     * Adding behaviors that require common header in dom here.
     */
    public componentDidMount(): void {
        super.componentDidMount();

        // Only set event listeners and autosuggest for search if top row is not disabled
        if (this.props.disableFirstRow) {
            return;
        }

        // Set refs and event listeners
        this.inputRef = document.getElementById(searchInputId);
        this.formRef = document.getElementById(searchBoxId);

        document.addEventListener("mousedown", this.handleClick);
        document.addEventListener("keydown", this.handleKeyDown);

        const { searchProps } = this.props;
        if (searchProps.keyPressCaptureEnabled) {
            this.onKeypressHandler = this.onKeypress.bind(this);
            document.body.addEventListener("keypress", this.onKeypressHandler, { capture: true });
        }

        // Try initializing autosuggest
        const delayBind = searchProps.autoSuggest && searchProps.autoSuggest.delayBind;
        if (delayBind
            && ((delayBind === AutoSuggestDelayBinding.OnBusinessSearchResultsReady
            && !searchProps.autoSuggest.businessSearchResultsEnabled)
            || delayBind !== AutoSuggestDelayBinding.OnBusinessSearchResultsReady)) {
            const autoSuggestInitProps = this.getAutoSuggestInitProps();
            initializeAutoSuggest(autoSuggestInitProps);
        }

        // set the TTF marker for search box
        this.markTimeToFunctional(KeyMetricTags.searchBox);

        // Using raf because elementtiming is only supported for text elements and images. All child web components either don't use
        // these types of elements or have them deep in the shadow dom
        window.requestAnimationFrame(() => {
            setTimeout(() => {
                this.markTimeToVisuallyReady(true);
                this.markTimeToVisuallyReady(true, undefined, KeyMetricTags.searchBox);
                Telemetry.observeSingleElement(this.formRef.parentElement);
            });
        });
    }

    /**
     * Uber handler that gets invoked for unhandled errors.
     * @param {Error} error error object.
     */
    public componentDidCatch(error: Error): void {
        const { appErrorCallback } = this.props;
        if (!appErrorCallback) {
            return;
        }

        appErrorCallback({
            ...CommonHeaderRenderError,
            message: "Render error in common header",
            pb: {
                ...CommonHeaderRenderError.pb,
                customMessage: `${error.message}\n${error.stack}`
            }
        });
    }

    /**
     * Render the experience.
     * @return {JSX.Element}
     */
    public render(): JSX.Element {
        super.render();

        const { managedClasses, meControlProps, disableFirstRow, commonHeaderTelemetryContext } = this.props;
        const designSystem = this.createDesignSystem();
        const meControlTelemetryTag = commonHeaderTelemetryContext && commonHeaderTelemetryContext.meControlTag;
        const meControlComponentProps = {
            ...meControlProps,
            useDarkTheme: this.state.isDarkMode,
            telemetryTag: meControlTelemetryTag
        };
        const styles = {
            backgroundColor: designSystem.backgroundColor
        };

        const commonHeader = (
            <FluentDesignSystemProvider use-defaults props={{ ...designSystem }}>
                <div className={managedClasses.root}
                    style={styles}
                    {...this.unhandledProps()}
                >
                    <div className={managedClasses.rootContainer}>
                        {!disableFirstRow &&
                            <div className={managedClasses.topContainer}>
                                {this.renderLogo()}
                                <div className={managedClasses.searchContainer}>{this.renderSearch()}</div>
                                <div className={managedClasses.userPreferencesContainer}>
                                    <div className={managedClasses.meControlContainer} {...meControlTelemetryTag}>
                                        <MeControlComponent {...meControlComponentProps} />
                                    </div>
                                    <div className={managedClasses.settingsContainer}>
                                        {this.renderSettings()}
                                    </div>
                                </div>
                            </div>
                        }
                        <div className={managedClasses.navigationContainer}>
                            {this.renderHamburgerMenu()}
                            <div className={managedClasses.contextualNavContainer}>{this.renderContextualNav()}</div>
                        </div>
                        {this.renderContextualSecondaryNav()}
                    </div>
                    {this.renderDivider()}
                </div>
            </FluentDesignSystemProvider>
        );

        if (this.props.includeWrapperDesignSystemProvider) {
            return (
                <DesignSystemProvider designSystem={designSystem}>
                    {commonHeader}
                </DesignSystemProvider>
            );
        } else {
            return commonHeader;
        }
    }

    public renderDivider(): JSX.Element {
        const { isContextualSecondaryNavEnabled } = this.props;
        if (!isContextualSecondaryNavEnabled) {
            return (<FluentDivider></FluentDivider>);
        }

        return null;
    }

    /**
     * Removes all event listeners
     */
    public componentWillUnmount(): void {
        document.removeEventListener("mousedown", this.handleClick);
        document.removeEventListener("keydown", this.handleKeyDown);

        const { searchProps } = this.props;
        if (searchProps && searchProps.keyPressCaptureEnabled && this.onKeypressHandler) {
            document.body.removeEventListener("keypress", this.onKeypressHandler);
        }
    }

    /**
     * Renders the hamburger menu with the passed in props
     */
    private renderHamburgerMenu(): JSX.Element {
        const { hamburgerMenuProps, handleRenderInfo, commonHeaderTelemetryContext, managedClasses, isContextualSecondaryNavEnabled } = this.props;
        if (!hamburgerMenuProps) {
            return null;
        }
        const { menuButtonTooltip, closeButtonTooltip, navItems, selectedNavId } = hamburgerMenuProps;
        const { hamburgerMenuTelemetry, closeHamburgerMetaTag, hamburgerMenuExpandedChangeTelemetryCallback } = commonHeaderTelemetryContext;

        if (isContextualSecondaryNavEnabled) {
            return null;
        }

        return <div className={managedClasses.hamburgerMenuContainer}>
            <MsftHamburgerMenu
                use-mobile-styles={false}
                menu-button-tooltip={menuButtonTooltip}
                close-button-tooltip={closeButtonTooltip}
                props={{
                    navItems,
                    selectedNavId,
                    hamburgerMenuButtonTelemetryTag: hamburgerMenuTelemetry && hamburgerMenuTelemetry.getMetadataTag(),
                    closeButtonTelemetryTag: closeHamburgerMetaTag
                }}
                events={{
                    change: handleRenderInfo,
                    "expanded-change": hamburgerMenuExpandedChangeTelemetryCallback
                }}
            ></MsftHamburgerMenu></div>;
    }

    /**
     * Renders the contextual nav with the passed in props
     */
    private renderContextualNav(): JSX.Element {
        const { contextualNavProps, handleRenderInfo, selectedNavItem, selectedNavMenuItem, commonHeaderTelemetryContext, isContextualSecondaryNavEnabled } = this.props;
        if (!contextualNavProps) {
            return null;
        }
        const { navGroups, localizedStrings, interestsNavData } = contextualNavProps;
        const { overflowMetaTag, contextualNavTelemetry } = commonHeaderTelemetryContext;
        const contextualNavTags = contextualNavTelemetry && contextualNavTelemetry.render();

        // As per Bug 30195255 (Temporary removal of "personalize" button from Common Header), we're hiding the personalize button until
        // IDX finishes the work on their side.
        return <FeedNavigation
            props={{
                navGroups,
                selectedNavItem,
                selectedNavMenuItem,
                interestsNavData,
                localizedStrings,
                hidePersonalizeButton: true,
                isTwoLevelNavigationEnabled: isContextualSecondaryNavEnabled,
                isSecondaryNav: false,
                overflowTelemetryTag: overflowMetaTag
            }}
            events={{ change: handleRenderInfo }}
            {...contextualNavTags}
        >
        </FeedNavigation>;
    }

    /**
     * Renders the contextual nav with the passed in props
     */
    private renderContextualSecondaryNav(): JSX.Element {
        // TODO: do we need to tweak any telemetry
        const { contextualSecondaryNavProps, handleRenderInfo, selectedSecondaryNavItem, selectedSecondaryNavMenuItem, commonHeaderTelemetryContext, isContextualSecondaryNavEnabled, managedClasses } = this.props;
        if (!isContextualSecondaryNavEnabled || selectedSecondaryNavMenuItem == null) {
            return null;
        }

        const { navGroups, localizedStrings, interestsNavData } = contextualSecondaryNavProps;
        const contextualNavTags = commonHeaderTelemetryContext && commonHeaderTelemetryContext.contextualNavTelemetry && commonHeaderTelemetryContext.contextualNavTelemetry.render();

        // As per Bug 30195255 (Temporary removal of "personalize" button from Common Header), we're hiding the personalize button until
        // IDX finishes the work on their side.
        return <div className={managedClasses.contextualSecondaryNavContainer}>
            <div className={managedClasses.secondaryFeedNavParent}>
                <FeedNavigation
                    props={{
                        navGroups,
                        selectedNavItem: selectedSecondaryNavItem,
                        selectedNavMenuItem: selectedSecondaryNavMenuItem,
                        interestsNavData,
                        localizedStrings,
                        hidePersonalizeButton: true,
                        isTwoLevelNavigationEnabled: true,
                        isSecondaryNav: true
                    }}
                    events={{ change: handleRenderInfo }}
                    {...contextualNavTags}
                >
                </FeedNavigation>
            </div>
        </div>;
    }

    /**
     * Renders the search box with the passed in components
     */
    private renderSearch(): JSX.Element {
        const { searchProps } = this.props;
        // Don't render if there are no props
        if (!searchProps) {
            return null;
        }

        const { action, autoFocus, autoComplete, formParameters, isOnImage, maxLength, minLength, placeholder, readOnly, spellCheck, target, title, telemetryContext, buttonLabel } = searchProps;
        const searchBoxProps = {
            action,
            autoFocus,
            autoComplete,
            formParameters,
            isOnImage,
            maxLength,
            minLength,
            placeholder,
            readOnly,
            spellCheck,
            target,
            title,
            id: searchInputId,
            buttonLabel,
            buttonTelemetryTag: telemetryContext.buttonMetaTag,
            inputTelemetryTag: telemetryContext.inputTag,
            onSubmit: this.onSearchSubmit,
            ...(telemetryContext.formTag)
        };

        return <SearchBox {...searchBoxProps}></SearchBox>;
    }

    /**
     * Renders the settings button and dialog
     */
    private renderSettings(): JSX.Element {
        const { settingsProps, commonHeaderTelemetryContext } = this.props;
        if (!settingsProps) {
            return null;
        }

        const { goToPersonalize, localizedStrings, hideMarketSelector } = settingsProps;
        const { settingsMetaTag, settingsPersonalizeButtonMetaTag, settingsCloseButtonMetaTag } = commonHeaderTelemetryContext;
        return <Settings
            props={{
                goToPersonalize,
                localizedStrings,
                hideMarketSelector,
                telemetryContext: {
                    settingsMetaTag,
                    personalizeButtonMetaTag: settingsPersonalizeButtonMetaTag,
                    settingsCloseButtonMetaTag
                }
            }}
        >
            {this.renderLanguageSelector()}
        </Settings>;
    }

    /**
     * Renders language selector dropdown
     *
     * TODO: Replace with select web component when available
     */
    private renderLanguageSelector(): JSX.Element {
        const { settingsProps, managedClasses, commonHeaderTelemetryContext } = this.props;
        const { settingsLanguageSelectorTag, languageOptions } = commonHeaderTelemetryContext;

        const onValueChange = (market: string) => {
            settingsProps.languageUpdatedCallback(market);
        };

        return (
            <div
                slot="language-dropdown"
                ref={(el) => this.marketSelectorRef = el}
                className={managedClasses.languageSelector}
            >
                <Select
                    id={languageSelectorId}
                    onValueChange={onValueChange}
                    defaultSelection={[getAppEnvironment().CurrentMarket || "en-us"]}
                    isMenuOpen={this.state.isMenuOpen}
                    jssStyleSheet={MarketSelectorStyles}
                    {...settingsLanguageSelectorTag}
                >
                    {
                        languageOptions && languageOptions.map((option, index) => {
                            const { market, telemetryTag } = option;
                            const { direction, ...selectProps } = market;
                            return <SelectOption
                                key={index}
                                dir={direction}
                                aria-posinset={index}
                                aria-setsize={this.markets.length}
                                {...selectProps}
                                {...telemetryTag} />;
                        })
                    }
                </Select>
            </div>
        );
    }

    /**
     * Renders the logo for the common header. Will try to render from config, but fallback to default Microsoft logo
     * if it's not available.
     */
    private renderLogo(): JSX.Element {
        const { managedClasses, lightLogo, darkLogo, commonHeaderTelemetryContext, logoAltText } = this.props;

        const defaultLogo = <span className={managedClasses.defaultLogo}>{MicrosoftLogoIcon}</span>;
        const lightModeLogo = lightLogo ? <span dangerouslySetInnerHTML={{__html: lightLogo}}></span> : defaultLogo;
        const darkModeLogo = darkLogo ? <span dangerouslySetInnerHTML={{__html: darkLogo}}></span> : defaultLogo;
        const { logoTag } = commonHeaderTelemetryContext;

        return <div
            className={managedClasses.logo}
            title={logoAltText}
            style={{"--common-header-logo-color": this.state.isDarkMode ? "var(--neutral-foreground-rest)" : "#737373"} }
            {...logoTag}>
            { this.state.isDarkMode ? darkModeLogo : lightModeLogo }
        </div>;
    }

    /**
     * Custom logic to handle page click events
     * @param event Click Event
     */
    private handleClick = (event: MouseEvent) => {
        if (!this.marketSelectorRef) {
            return;
        }

        const target = event.target as HTMLElement;
        const isDropDownChild = this.marketSelectorRef.contains(target);

        // not a child, close menu
        if (!isDropDownChild) {
            this.setState({ isMenuOpen: false });
            return;
        }

        // ignore scroll bar and right clicks
        const direction = document.dir && document.dir.toLowerCase();
        const isSelectMenu = target.getAttribute && target.getAttribute("class") && target.getAttribute("class").includes("select_menu");
        if (event.offsetX < target.clientWidth && direction === Direction.rtl && isSelectMenu && event.buttons === 1) {
            return;
        }

        if (event.offsetX < target.clientWidth && event.buttons === 1) {
            target.click && target.click();
            this.setState({ isMenuOpen: !this.state.isMenuOpen });
        }
    };

    /**
     * Key down handler for the document
     * @param e Keyboard event object
     */
    private handleKeyDown = (event) => {
        if (!this.marketSelectorRef) {
            return;
        }

        if (this.state.isMenuOpen && event.keyCode === keyCodeEscape) {
            this.setState({ isMenuOpen: false });
            return;
        }

        if (this.marketSelectorRef.contains(event.target) && (event.keyCode === keyCodeEnter || event.keyCode === keyCodeSpace)) {
            this.setState({ isMenuOpen: !this.state.isMenuOpen });
        }
    };

    /**
     * Handler for keypress events
     * @param event keyboard event
     */
    private onKeypress(event: React.KeyboardEvent<HTMLElement>): void {
        const target = event.target as Element;
        const targetTagName = target.tagName.toLowerCase();

        // If user is typing in another input element, ignore
        if (targetTagName === "input" || targetTagName === "textarea" || targetTagName === "msft-feed-layout") {
            return;
        }

        // Only capture alphanumeric characters
        if (event.charCode > 32 && event.charCode <= 126 && target.id !== searchInputId) {
            this.inputRef.focus();
        }
    }

    /**
     * Handler for search submit events
     */
    private onSearchSubmit = (): void => {
        const { searchProps } = this.props;
        if (!searchProps || !searchProps.telemetryCallback) {
            return;
        }

        const contentItem = TelemetryDOM.get(this.formRef);
        contentItem.contract.destinationUrl = searchProps.action;
        TelemetryDOM.setAttribute(this.formRef, contentItem.contract);
        searchProps.telemetryCallback(this.formRef, TelemetryActionType.Submit, TelemetryBehaviorType.TextSearch);
    };

    /**
     * Gets the autosuggest init props from the common header props
     */
    private getAutoSuggestInitProps(): AutoSuggestInitProperties {
        const { searchProps } = this.props;
        if (!searchProps) {
            return null;
        }

        const { autoSuggest, requestId, telemetryCallback, action } = searchProps;
        return {
            autoSuggestProperties: autoSuggest,
            requestId,
            telemetryCallback,
            action,
            searchBoxContext: this,
            isDarkMode: this.state.isDarkMode
        };
    }

    /**
     * Create design sytem for CommonHeader
     */
    private createDesignSystem(): typeof DesignSystemDefaults {
        return {
            ...DesignSystemDefaults,
            backgroundColor: this.getBackgroundColor(),
            direction: document.dir && document.dir.toLowerCase() === "rtl" ? Direction.rtl : Direction.ltr
        };
    }
    /**
    * Determine the background color based on the theme and if darkmode is disabled.
    */
    private getBackgroundColor(): string {
        return this.state.isDarkMode ?
            neutralLayerCardContainer(() => blackHexColor)(DesignSystemDefaults) :
            neutralLayerFloating(() => whiteHexColor)(DesignSystemDefaults);
    }
}

/**
 * Interface for Common Header Component State
 */
export interface CommonHeaderComponentState {
    /** Language dropdown open state */
    isMenuOpen: boolean;

    /** Dark mode state. Controls the background color for light and dark themes */
    isDarkMode: boolean;
}

declare module "@msnews/feed-navigation" {
    interface FeedNavigation extends React.Component<{
        props: FeedNavigationData<ContextualNavItem>;
        events: any;
    } & React.HTMLAttributes<FeedNavigation>> { }
}

declare module "@msnews/settings" {
    interface Settings extends React.Component<{
        props: BaseSettingsProps;
        events?: any;
    } & React.HTMLAttributes<Settings>> { }
}

/**
 * This file needs to exist to augment the existing typings from FluentDesignSystemProvider
 * See https://www.typescriptlang.org/docs/handbook/declaration-merging.html#module-augmentation
 */
declare module "@fluentui/web-components" {
    interface FluentDesignSystemProvider extends React.Component<{
        "use-defaults"?: boolean,
        backgroundColor?: string,
        props?: any,
        direction?: string
    } & React.HTMLAttributes<FluentDesignSystemProvider>> { }

    interface FluentDivider extends React.Component<React.HTMLAttributes<FluentDivider>> { }
}

declare module "@fast/components" {
    interface MsftHamburgerMenu extends React.Component<{
        "use-mobile-styles": boolean,
        "menu-button-tooltip": string,
        "close-button-tooltip": string,
        props: {
            navItems: HamburgerMenuItemInternal[],
            selectedNavId: string,
            hamburgerMenuButtonTelemetryTag: string,
            closeButtonTelemetryTag: string
        },
        events: any
    } & React.HTMLAttributes<MsftHamburgerMenu>> { }
}
